import type { ReportTypeType } from '@coursera/event-pulse-types';

import { type ReportAction, type ReportType, type ReportV2 } from 'bundles/enterprise-admin-analytics/types';

import _t from 'i18n!nls/enterprise-admin-analytics';

export const getAllInOneReport = (): ReportV2 => ({
  reportType: 'ALL_IN_ONE',
  name: _t('All in one'),
  description: _t(
    'Provides a convenient zip file containing all available reports, giving you a comprehensive collection of insights and metrics in a single downloadable package.'
  ),
});

export const getLearnerActivityProgressReport = (): ReportV2 => ({
  reportType: 'LEARNER_ENGAGEMENT',
  name: _t('Learner activity & progress'),
  description: _t(
    'Offers a comprehensive view of how learners engage across programs and content types—including Learning Paths, Specializations, and Courses. This category helps admins understand overall participation, progress, and consumption trends to drive targeted interventions.'
  ),
  previouslyKnownAs: _t('Usage Report, Membership Report, or Content Activity Report'),
  whatsNew: {
    text: _t('Introduction of the'),
    highlight: _t('Learning Paths Activity Report'),
  },
});

export const getSkillDevelopmentReport = (): ReportV2 => ({
  reportType: 'LEARNER_SKILLS',
  name: _t('Skill development'),
  description: _t(
    'Delivers a holistic picture of skill growth within programs, helping admins track development at both the individual and cohort levels. This category includes insights into top skills learned, depth of mastery, and benchmarking across industries.'
  ),
  isNew: true,
  includes: [
    { name: _t('Skill Breakdown Report'), description: _t('Program level skills overview') },
    { name: _t('Skills Leaderboard Report'), description: _t('Insights into learner-level skill gains') },
    { name: _t('Skill Benchmarking Report'), description: _t('Industry and content-type comparisons') },
  ],
});

export const getLearnerPerformanceReport = (): ReportV2 => ({
  reportType: 'LEARNER_PERFORMANCE',
  name: _t('Learner performance'),
  description: _t(
    'Provides detailed visibility into learner outcomes, including grades, assessments, and academic integrity signals. Admins can identify high performers, struggling learners, and potential integrity issues to support timely coaching and intervention.'
  ),
  previouslyKnownAs: _t('Gradebook Report and Plagiarism Report'),
});

export const getCurriculumReport = (): ReportV2 => ({
  reportType: 'CURRICULUM',
  name: _t('Curriculum'),
  description: _t(
    'Provides details on courses and specializations in curated collections, with item details and course links.'
  ),
});

export const getMonthlyDigestReport = (): ReportV2 => ({
  reportType: 'MONTHLY_DIGEST_EMAIL',
  name: _t('Monthly digest'),
  description: _t(
    "Regular updates on your organization's learners and their engagement on Coursera (Ex: shows learning hours, course completion details, trending skills etc)"
  ),
});

export const getVspProgressReport = (): ReportV2 => ({
  reportType: 'VERIFIED_SKILL_PATH_PROGRESS',
  name: _t('Verified Skill Paths Progress Report'),
  description: _t(
    'Download learner progress for those enrolled in Verified Skill Paths, including skills mastered and assessment activity within each path.'
  ),
  isNew: true,
});

export const getAllReportDefinitions = (): ReportV2[] => [
  getAllInOneReport(),
  getLearnerActivityProgressReport(),
  getSkillDevelopmentReport(),
  getVspProgressReport(),
  getLearnerPerformanceReport(),
  getCurriculumReport(),
  getMonthlyDigestReport(),
];

// Function to avoid calling _t() at module load time (SSR-safe)
export const getReportDefinitionsMap = (): Record<ReportType, ReportV2> => ({
  ALL_IN_ONE: getAllInOneReport(),
  LEARNER_ENGAGEMENT: getLearnerActivityProgressReport(),
  LEARNER_SKILLS: getSkillDevelopmentReport(),
  LEARNER_PERFORMANCE: getLearnerPerformanceReport(),
  CURRICULUM: getCurriculumReport(),
  MONTHLY_DIGEST_EMAIL: getMonthlyDigestReport(),
  VERIFIED_SKILL_PATH_PROGRESS: getVspProgressReport(),
});

export const REPORT_ACTIONS_FOR_URL = {
  PREVIEW_REPORT: 'preview',
  EDIT_SCHEDULED_REPORT: 'edit',
  CONFIGURE_REPORT: 'configure',
  CANCEL_SCHEDULED_REPORT: 'cancel',
  SCHEDULE_MONTHLY_DIGEST_REPORT: 'schedule-monthly-digest',
} satisfies Record<ReportAction, string>;

export const REPORT_TYPE_FOR_URL: Record<ReportType, string> = {
  ALL_IN_ONE: 'all-in-one',
  LEARNER_ENGAGEMENT: 'learner-engagement',
  LEARNER_SKILLS: 'learner-skills',
  LEARNER_PERFORMANCE: 'learner-performance',
  CURRICULUM: 'curriculum',
  MONTHLY_DIGEST_EMAIL: 'monthly-digest',
  VERIFIED_SKILL_PATH_PROGRESS: 'verified-skill-path-progress',
};

export const REPORT_TYPE_IN_CAMEL_CASE: Record<ReportType, string> = {
  ALL_IN_ONE: 'allInOne',
  LEARNER_ENGAGEMENT: 'learnerEngagement',
  LEARNER_SKILLS: 'learnerSkills',
  LEARNER_PERFORMANCE: 'learnerPerformance',
  CURRICULUM: 'curriculum',
  MONTHLY_DIGEST_EMAIL: 'monthlyDigestEmail',
  VERIFIED_SKILL_PATH_PROGRESS: 'verifiedSkillPathProgress',
};

export const REPORT_TYPE_FOR_TRACKING: Record<ReportType, ReportTypeType> = {
  ALL_IN_ONE: 'all_in_one',
  LEARNER_ENGAGEMENT: 'learner_activity_and_progress',
  LEARNER_SKILLS: 'skill_development',
  LEARNER_PERFORMANCE: 'learner_performance',
  CURRICULUM: 'curriculum',
  MONTHLY_DIGEST_EMAIL: 'monthly_digest',
  VERIFIED_SKILL_PATH_PROGRESS: 'verified_skill_path_progress' as ReportTypeType,
};

/**
 * Polling configuration for report generation
 * Defines how often to poll and when to timeout for each report type
 */
export type ReportPollingConfig = {
  frequency: number; // Polling frequency in milliseconds
  timeout: number; // Timeout in milliseconds
};

/**
 * Report-specific polling configurations
 * Customize these values based on report complexity and expected generation time
 */
export const REPORT_POLLING_CONFIG: Record<Exclude<ReportType, 'MONTHLY_DIGEST_EMAIL'>, ReportPollingConfig> = {
  ALL_IN_ONE: {
    frequency: 45 * 1000, // 45 seconds (larger report, poll less frequently)
    timeout: 6 * 60 * 60 * 1000, // 6 hours (complex report, needs more time)
  },
  LEARNER_ENGAGEMENT: {
    frequency: 30 * 1000, // 30 seconds
    timeout: 4 * 60 * 60 * 1000, // 4 hours
  },
  LEARNER_SKILLS: {
    frequency: 30 * 1000, // 30 seconds
    timeout: 4 * 60 * 60 * 1000, // 4 hours
  },
  LEARNER_PERFORMANCE: {
    frequency: 30 * 1000, // 30 seconds
    timeout: 4 * 60 * 60 * 1000, // 4 hours
  },
  CURRICULUM: {
    frequency: 30 * 1000, // 30 seconds
    timeout: 4 * 60 * 60 * 1000, // 4 hours
  },
  VERIFIED_SKILL_PATH_PROGRESS: {
    frequency: 30 * 1000, // 30 seconds
    timeout: 4 * 60 * 60 * 1000, // 4 hours
  },
};
